import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import {
  CustomScrollbarContainer,
  CustomScrollbarLeftArrow,
  CustomScrollbarLeftButton,
  CustomScrollbarRightArrow,
  CustomScrollbarRightButton,
  CustomScrollbarThumb,
  CustomScrollbarTrack,
  CustomScrollbarTrackBox,
  CustomScrollbarWrapper,
  CustomScrollbarWrapperInner,
} from './index.styles'

const CustomScrollbar = ({ contentRef }: { contentRef: RefObject<HTMLDivElement> }) => {
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [isScrollbarVisible, setScrollbarVisible] = useState<boolean>(true)
  const [scrollStartPosition, setScrollStartPosition] = useState<number>(0)
  const [thumbLeftPosition, setThumbLeftPosition] = useState<number>(0)
  const [initialScrollLeft, setInitialScrollLeft] = useState<number>(0)
  const [isDragging, setIsDragging] = useState(false)

  const handleResize = (ref: HTMLDivElement, trackSize: number) => {
    const { clientWidth, scrollWidth } = ref

    setScrollbarVisible(clientWidth !== scrollWidth)

    setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20))
  }

  const handleScrollButton = (direction: 'left' | 'right') => {
    const { current } = contentRef
    if (current) {
      const scrollAmount = direction === 'right' ? 100 : -100
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  const preventDefaultWhenUsingScrollbar = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      if (e.target === scrollThumbRef.current || e.target === scrollTrackRef.current) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [scrollThumbRef.current, scrollTrackRef.current],
  )

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      preventDefaultWhenUsingScrollbar(e)

      const { current: trackCurrent } = scrollTrackRef
      const { current: contentCurrent } = contentRef

      if (e.target === scrollThumbRef.current) {
        return
      }

      if (trackCurrent && contentCurrent) {
        const { clientX } = e
        const target = e.target as HTMLDivElement
        const rect = target.getBoundingClientRect()
        const trackLeft = rect.left
        const thumbOffset = -(thumbWidth / 2)
        const clickRatio = (clientX - trackLeft + thumbOffset) / trackCurrent.clientWidth
        const scrollAmount = Math.floor(clickRatio * contentCurrent.scrollWidth)
        contentCurrent.scrollTo({
          left: scrollAmount,
          behavior: 'smooth',
        })
      }
    },
    [thumbWidth],
  )

  const handleThumbPosition = useCallback(() => {
    if (!contentRef.current || !scrollTrackRef.current || !scrollThumbRef.current) {
      return
    }
    const { scrollLeft: contentLeft, scrollWidth: contentWidth } = contentRef.current
    const { clientWidth: trackWidth } = scrollTrackRef.current
    const newLeft = (+contentLeft / +contentWidth) * trackWidth
    setThumbLeftPosition(Math.min(newLeft, trackWidth - thumbWidth))
  }, [])

  const handleThumbMousedown = useCallback((e: React.MouseEvent) => {
    preventDefaultWhenUsingScrollbar(e)
    setScrollStartPosition(e.clientX)
    if (contentRef.current) setInitialScrollLeft(contentRef.current.scrollLeft)
    setIsDragging(true)
  }, [])

  const handleThumbMouseup = useCallback(
    (e: MouseEvent) => {
      preventDefaultWhenUsingScrollbar(e)
      if (isDragging) {
        setIsDragging(false)
      }
    },
    [isDragging],
  )

  const handleThumbMousemove = useCallback(
    (event: MouseEvent): void => {
      preventDefaultWhenUsingScrollbar(event)
      if (isDragging && contentRef.current) {
        const { scrollWidth: contentScrollWidth, offsetWidth: contentOffsetWidth } = contentRef.current
        const deltaX = (event.clientX - scrollStartPosition) * (contentOffsetWidth / thumbWidth)

        // eslint-disable-next-line no-param-reassign
        contentRef.current.scrollLeft = Math.min(initialScrollLeft + deltaX, contentScrollWidth - contentOffsetWidth)
      }
    },
    [isDragging, scrollStartPosition, thumbWidth],
  )

  useEffect(() => {
    const currentRefObject = contentRef.current
    if (currentRefObject && scrollTrackRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        handleResize(currentRefObject, scrollTrackRef.current?.clientWidth || currentRefObject.clientWidth)
      })
      resizeObserver.observe(currentRefObject)

      const contentMutationObserver = new MutationObserver(() =>
        handleResize(currentRefObject, scrollTrackRef.current?.clientWidth || currentRefObject.clientWidth),
      )
      contentMutationObserver.observe(contentRef.current, {
        childList: true,
        subtree: true,
      })

      currentRefObject.addEventListener('scroll', handleThumbPosition)

      return () => {
        contentMutationObserver.disconnect()
        resizeObserver.unobserve(currentRefObject)
        currentRefObject.removeEventListener('scroll', handleThumbPosition)
      }
    }
    return undefined
  }, [])

  useEffect(() => {
    document.addEventListener('mousemove', handleThumbMousemove)
    document.addEventListener('mouseup', handleThumbMouseup)
    document.addEventListener('mouseleave', handleThumbMouseup)
    return () => {
      document.removeEventListener('mousemove', handleThumbMousemove)
      document.removeEventListener('mouseup', handleThumbMouseup)
      document.removeEventListener('mouseleave', handleThumbMouseup)
    }
  }, [handleThumbMousemove, handleThumbMouseup])

  return (
    <CustomScrollbarWrapper>
      {isScrollbarVisible && (
        <CustomScrollbarWrapperInner>
          <CustomScrollbarContainer data-testid="custom-scrollbar-container">
            <CustomScrollbarLeftButton
              size="small"
              variant="contained"
              sx={{ padding: '4px', minWidth: '12px' }}
              onClick={() => handleScrollButton('left')}
            >
              <CustomScrollbarLeftArrow />
            </CustomScrollbarLeftButton>
            <CustomScrollbarTrackBox>
              <CustomScrollbarTrack ref={scrollTrackRef} onClick={handleTrackClick} style={{ cursor: 'pointer' }}>
                <CustomScrollbarThumb
                  ref={scrollThumbRef}
                  onMouseDown={handleThumbMousedown}
                  style={{
                    width: `${thumbWidth}px`,
                    left: `${thumbLeftPosition}px`,
                    cursor: isDragging ? 'grabbing' : 'grab',
                  }}
                >
                  | | | | |
                </CustomScrollbarThumb>
              </CustomScrollbarTrack>
            </CustomScrollbarTrackBox>
            <CustomScrollbarRightButton
              size="small"
              variant="contained"
              sx={{ padding: '4px', minWidth: '12px' }}
              onClick={() => handleScrollButton('right')}
            >
              <CustomScrollbarRightArrow />
            </CustomScrollbarRightButton>
          </CustomScrollbarContainer>
        </CustomScrollbarWrapperInner>
      )}
    </CustomScrollbarWrapper>
  )
}

export default CustomScrollbar
